import React, {useContext, useMemo} from 'react';
import {Modal, ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {FontAwesome, FontAwesome5, Ionicons, MaterialCommunityIcons, MaterialIcons,} from '@expo/vector-icons';
import {useTranslation} from 'react-i18next';
import '../../i18n';
import InventoryForm from '../components/InventoryForm';
import TodoList from '../components/TodoList';
import DateTimeModal from '../components/DateTimeModal';
import StandModal from '../components/StandModal';
import {useOfflineSyncContext} from '../helpers/OfflineSyncContext';
import {TourContext} from '../helpers/TourContext';
import {OrdersContext} from '../helpers/OrdersContext';
import {useMail} from '../helpers/MailContext';
import Observables from '@happy-gastro/global-observables';
import TransportStatusBadge from '../components/TransportStatusBadge';
import {useTheme} from '../helpers/ThemeContext';
import {useThemedStyles} from '../helpers/useThemedStyles';
import {isModuleEnabled} from '../config/featureFlags';
import useAiAssistantGate from './assistant/components/useAiAssistantGate';
import {useChatHubBadge} from '../helpers/teamChat/useChatHubBadge';
import {haptics} from '../helpers/haptics';

const renderMenuIcon = ({family, name, color = '#111827', size = 24}) => {
    switch (family) {
        case 'FontAwesome':
            return <FontAwesome name={name} size={size} color={color} />;
        case 'FontAwesome5':
            return <FontAwesome5 name={name} size={size} color={color} />;
        case 'Ionicons':
            return <Ionicons name={name} size={size} color={color} />;
        case 'MaterialCommunityIcons':
            return <MaterialCommunityIcons name={name} size={size} color={color} />;
        case 'MaterialIcons':
        default:
            return <MaterialIcons name={name} size={size} color={color} />;
    }
};

const SettingsActionRow = ({item, isLast = false, styles, theme}) => {
    const isHighlighted = item.variant === 'highlight';
    const isDisabled = item.disabled;

    return (
        <TouchableOpacity
            activeOpacity={isDisabled ? 1 : 0.85}
            disabled={isDisabled}
            onPress={() => {
                haptics.select();
                item.onPress?.();
            }}
            style={[
                styles.settingsRow,
                !isLast && styles.settingsRowBorder,
                isHighlighted && styles.settingsRowHighlight,
                isDisabled && styles.settingsRowDisabled,
            ]}
        >
            <View style={[
                styles.rowIconBadge,
                isHighlighted && styles.rowIconBadgeHighlight,
                isDisabled && styles.rowIconBadgeDisabled,
            ]}>
                {renderMenuIcon({
                    family: item.iconFamily,
                    name: item.icon,
                    color: isHighlighted ? '#c2410c' : (isDisabled ? theme.colors.subText : theme.colors.text),
                    size: item.iconSize || 22,
                })}
            </View>
            <View style={styles.rowContent}>
                <Text style={[
                    styles.rowTitle,
                    isHighlighted && styles.rowTitleHighlight,
                    isDisabled && styles.rowTitleDisabled,
                ]}>
                    {item.title}
                </Text>
                {item.subtitle ? (
                    <Text style={[
                        styles.rowSubtitle,
                        isHighlighted && styles.rowSubtitleHighlight,
                        isDisabled && styles.rowTitleDisabled,
                    ]}>
                        {item.subtitle}
                    </Text>
                ) : null}
            </View>

            {isDisabled ? (
                <View style={styles.disabledPill}>
                    <MaterialIcons name="lock-outline" size={14} color={theme.colors.subText} />
                </View>
            ) : (
                <MaterialIcons
                    name="chevron-right"
                    size={22}
                    color={isHighlighted ? '#c2410c' : theme.colors.subText}
                />
            )}
        </TouchableOpacity>
    );
};

const FunctionsScreen = ({navigation}) => {
    const {t} = useTranslation();
    const {theme, isDark} = useTheme();
    const styles = useThemedStyles(buildStyles);
    const {width} = useWindowDimensions();
    const [inventoryModalVisible, setInventoryModalVisible] = React.useState(false);
    const [todoModalVisible, setTodoModalVisible] = React.useState(false);
    const [showStand, setShowStand] = React.useState(false);
    const [showdatemodal, setShowDateModal] = React.useState(false);
    const [isExtendedDayClosing, setIsExtendedDayClosing] = React.useState(false);
    const [isFiscal, setIsFiscal] = React.useState(false);
    const {pendingCount, failedCount, syncingCount} = useOfflineSyncContext();
    const {startTour} = useContext(TourContext);
    const {activeTransport = 'polling'} = useContext(OrdersContext) || {};
    const {unreadCount} = useMail();
    // "Ask anything" AI assistant — build-flag + store-level gate (function.aiAssistant).
    const aiAssistantEnabled = useAiAssistantGate();
    // Team Chat + meetings — same entry pattern as Mail; hidden when chat is excluded.
    // Messenger hub entry: team chat + live support unread combined.
    const {visible: teamChatVisible, total: teamChatUnread} = useChatHubBadge();
    // Procurement — build-time switchboard only. The screens themselves resolve
    // the store's licence and the user's permissions, so a till that may not
    // order still gets told why instead of silently losing the rows.
    const procurementEnabled = isModuleEnabled('procurement');

    React.useEffect(() => {
        let mounted = true;

        const loadSettings = async () => {
            const [selectedCashierDevice, extendedDayClosing] = await Promise.all([
                AsyncStorage.getItem('selectedCashierDevice'),
                AsyncStorage.getItem('extendedDayClosing'),
            ]);

            if (!mounted) {
                return;
            }

            setIsFiscal(Boolean(selectedCashierDevice));
            setIsExtendedDayClosing(extendedDayClosing === 'true');
        };

        loadSettings();

        return () => {
            mounted = false;
        };
    }, []);

    const openStatisticsScreen = React.useCallback(() => {
        const parentNavigation = navigation.getParent?.();
        const parentState = parentNavigation?.getState?.();
        const parentScreen = parentState?.routes?.[parentState.index]?.name || 'HomeScreen';

        navigation.navigate('StatisticsScreen', {
            returnTo: {
                parentScreen,
                nestedScreen: 'Functions',
            },
        });
    }, [navigation]);

    const openDayClosingScreen = React.useCallback(() => {
        navigation.navigate(isExtendedDayClosing ? 'DayClosingScreenExtended' : 'DayClosingScreenBasic');
    }, [isExtendedDayClosing, navigation]);

    const toggleInventoryModal = React.useCallback(() => {
        setInventoryModalVisible((previous) => !previous);
    }, [setInventoryModalVisible]);

    const toggleTodoModal = React.useCallback(() => {
        setTodoModalVisible((previous) => !previous);
    }, [setTodoModalVisible]);

    const openConsumableStock = React.useCallback(() => {
        setShowDateModal(true);
    }, [setShowDateModal]);

    const openMailScreen = React.useCallback(() => {
        navigation.navigate('MailHomeScreen');
    }, [navigation]);

    const openAssistantScreen = React.useCallback(() => {
        navigation.navigate('AIAssistantScreen');
    }, [navigation]);

    const openTeamChat = React.useCallback(() => {
        navigation.navigate('ChatHomeScreen');
    }, [navigation]);

    const openDialer = React.useCallback(() => {
        navigation.navigate('DialerScreen');
    }, [navigation]);

    const restartTutorial = React.useCallback(() => {
        // Return to the Sales home screen, then (re)start the guided tour. The
        // navigator only knows "HomeScreen" — navigating to a non-existent
        // "Sale"/"BigSale" route used to throw for some users.
        navigation.navigate('HomeScreen');
        globalThis.setTimeout(() => {
            startTour({
                force: true,
                source: 'manual',
                delay: 150,
            });
        }, 120);
    }, [navigation, startTour]);

    const statusChips = useMemo(() => ([
        {
            key: 'cashier',
            label: isFiscal ? t('functions_chip_cashier_connected') : t('functions_chip_cashier_not_selected'),
            active: isFiscal,
        },
        {
            key: 'closing',
            label: isExtendedDayClosing ? t('functions_chip_extended_day_closing') : t('functions_chip_normal_day_closing'),
            active: isExtendedDayClosing,
        },
    ]), [isExtendedDayClosing, isFiscal, t]);

    const metrics = useMemo(() => {
        const screenPadding = width >= 1200 ? 28 : width >= 768 ? 24 : 16;
        const contentMaxWidth = width >= 768
            ? Math.min(width - (screenPadding * 2), 860)
            : width - (screenPadding * 2);

        return {
            contentMaxWidth,
            screenPadding,
        };
    }, [width]);

    const featuredActions = useMemo(() => ([
        {
            key: 'daily-traffic',
            title: t('daily_traffic'),
            subtitle: t('traffic_report'),
            iconFamily: 'MaterialCommunityIcons',
            icon: 'chart-line',
            onPress: openStatisticsScreen,
        },
        {
            key: 'financial-day-close',
            title: t('financial_day_closing'),
            subtitle: t('functions_day_closing_subtitle'),
            iconFamily: 'MaterialCommunityIcons',
            icon: 'lock-outline',
            onPress: openDayClosingScreen,
            variant: 'highlight',
        },
        {
            key: 'app-tutorial',
            title: t('functions_tutorial_title'),
            subtitle: t('functions_tutorial_subtitle'),
            iconFamily: 'MaterialCommunityIcons',
            icon: 'map-search-outline',
            onPress: restartTutorial,
        },
    ]), [openDayClosingScreen, openStatisticsScreen, restartTutorial, t]);

    const sections = useMemo(() => {
        const result = [
            {
                key: 'tasks',
                title: t('tasks'),
                items: [
                    {
                        key: 'pre-orders',
                        title: t('pre_orders'),
                        subtitle: t('functions_pre_orders_subtitle'),
                        iconFamily: 'MaterialIcons',
                        icon: 'dynamic-feed',
                        onPress: () => navigation.navigate('PendingOrdersScreen'),
                    },
                    {
                        key: 'todo',
                        title: t('to_do'),
                        subtitle: t('functions_todo_subtitle'),
                        iconFamily: 'FontAwesome',
                        icon: 'list-ul',
                        onPress: toggleTodoModal,
                    },
                    {
                        key: 'bookings',
                        title: t('bookings'),
                        subtitle: t('functions_bookings_subtitle'),
                        iconFamily: 'FontAwesome',
                        icon: 'calendar-check-o',
                        onPress: () => navigation.navigate('TablesBookingsScreen'),
                    },
                ],
            },
            {
                key: 'warehouse',
                title: t('warehouse_operations'),
                items: [
                    {
                        key: 'inventory',
                        title: t('inventory'),
                        subtitle: t('functions_inventory_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'book-open-page-variant-outline',
                        // The old StandModal only logged its payload (never booked);
                        // the stand count screen books the stand and has draft mode.
                        onPress: () => navigation.navigate('StockCountScreen'),
                    },
                    {
                        key: 'receiving',
                        title: t('receiving'),
                        subtitle: t('functions_receiving_subtitle'),
                        iconFamily: 'FontAwesome5',
                        icon: 'file-invoice',
                        onPress: toggleInventoryModal,
                    },
                    {
                        key: 'consumable-stock',
                        title: t('consumable_stock'),
                        subtitle: t('functions_consumable_stock_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'chart-bell-curve',
                        onPress: openConsumableStock,
                    },
                    {
                        key: 'waste',
                        title: t('warehouse.writeOff.title_waste'),
                        subtitle: t('warehouse.writeOff.tile_waste_sub'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'delete-sweep-outline',
                        onPress: () => navigation.navigate('StockWriteOffScreen', {mode: 'waste'}),
                    },
                ],
            },
            // Beszerzés — the POS-side entry into the procurement module. A till
            // operator is the person who notices the stock is gone, so "Új
            // igénylés" is one tap from the same screen as the stocktake and the
            // goods intake; approving and ordering then happens on the same
            // documents the admin and the management app work with. Dropped
            // entirely when the build ships without the module
            // (config/featureFlags.js).
            ...(procurementEnabled ? [{
                key: 'procurement',
                title: t('procurement_module_eyebrow', {defaultValue: 'Beszerzés'}),
                items: [
                    {
                        key: 'procurement-new-requisition',
                        title: t('procurement_requisitions_new', {defaultValue: 'Új igénylés'}),
                        subtitle: t('procurement_functions_new_requisition_subtitle', {
                            defaultValue: 'Kérd be, ami elfogyott',
                        }),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'playlist-plus',
                        onPress: () => navigation.navigate('ProcurementRequisitionsScreen'),
                    },
                    {
                        key: 'procurement-orders',
                        title: t('procurement_page_title_orders', {defaultValue: 'Megrendelések'}),
                        subtitle: t('procurement_functions_orders_subtitle', {
                            defaultValue: 'Beszállítói megrendelések állapota',
                        }),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'cart-outline',
                        onPress: () => navigation.navigate('ProcurementOrdersScreen'),
                    },
                    {
                        key: 'procurement-hub',
                        title: t('procurement_page_title_dashboard', {defaultValue: 'Beszerzés'}),
                        subtitle: t('procurement_functions_hub_subtitle', {
                            defaultValue: 'Mit kell rendelni, mi vár jóváhagyásra',
                        }),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'view-dashboard-outline',
                        onPress: () => navigation.navigate('ProcurementHomeScreen'),
                    },
                ],
            }] : []),
            {
                key: 'printing',
                title: t('printers_and_profiles'),
                items: [
                    {
                        key: 'printer-profiles',
                        title: t('printer_profiles_title'),
                        subtitle: t('functions_printer_profiles_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'printer-settings',
                        onPress: () => navigation.navigate('PrinterProfilesScreen'),
                    },
                ],
            },
            {
                key: 'expenses',
                title: t('expenses'),
                items: [
                    {
                        key: 'expenses-main',
                        title: t('expenses'),
                        subtitle: t('functions_expenses_subtitle'),
                        iconFamily: 'MaterialIcons',
                        icon: 'currency-exchange',
                        onPress: () => navigation.navigate('ExpensesScreen'),
                    },
                ],
            },
            {
                key: 'haccp',
                title: t('haccp'),
                items: [
                    {
                        key: 'haccp-temperature',
                        title: t('haccp_temperature_title'),
                        subtitle: t('haccp_temperature_menu_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'thermometer-lines',
                        onPress: () => navigation.navigate('HaccpScreen'),
                    },
                ],
            },
            // "Ask anything" AI assistant — same entry pattern as Mail below,
            // section dropped entirely when the store/build disables the module.
            ...(aiAssistantEnabled ? [{
                key: 'assistant',
                title: t('assistant_title', 'AI asszisztens'),
                items: [
                    {
                        key: 'assistant-chat',
                        title: t('assistant_title', 'AI asszisztens'),
                        subtitle: t('assistant_menu_subtitle', 'Kérdezz bármit — adatok, riportok és műveletek'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'robot-outline',
                        onPress: openAssistantScreen,
                    },
                ],
            }] : []),
            ...(teamChatVisible ? [{
                key: 'team-chat',
                title: t('team_chat_hub_title'),
                items: [
                    {
                        key: 'team-chat-home',
                        title: t('team_chat_hub_title'),
                        subtitle: teamChatUnread > 0
                            ? t('team_chat_unread_count', {count: teamChatUnread})
                            : t('team_chat_menu_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'forum-outline',
                        onPress: openTeamChat,
                    },
                ],
            }] : []),
            {
                key: 'mail',
                title: t('mail_title'),
                items: [
                    {
                        key: 'mail-home',
                        title: t('mail_title'),
                        subtitle: unreadCount > 0
                            ? t('mail_unread_count', {count: unreadCount})
                            : t('mail_menu_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'email-outline',
                        onPress: openMailScreen,
                    },
                ],
            },
            {
                key: 'voip',
                title: t('voip_title', 'Telefon (VoIP)'),
                items: [
                    {
                        key: 'voip-dialer',
                        title: t('voip_dialer', 'Tárcsázó'),
                        subtitle: t('voip_dialer_subtitle', 'Hívásindítás, névjegyek és egyenleg'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'dialpad',
                        onPress: openDialer,
                    },
                    {
                        key: 'voip-directory',
                        title: t('voip_directory', 'Névjegyzék'),
                        subtitle: t('voip_directory_subtitle', 'Névjegyek kezelése és keresés'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'account-multiple-outline',
                        onPress: () => navigation.navigate('DirectoryScreen'),
                    },
                    {
                        key: 'voip-history',
                        title: t('voip_call_history', 'Híváslista'),
                        subtitle: t('voip_history_subtitle', 'Bejövő és kimenő hívások'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'phone-log',
                        onPress: () => navigation.navigate('CallHistoryScreen'),
                    },
                    {
                        key: 'voip-settings',
                        title: t('voip_settings', 'VoIP beállítások'),
                        subtitle: t('voip_settings_subtitle', 'Szolgáltató és hívás egyenleg'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'phone-settings-outline',
                        onPress: () => navigation.navigate('VoipSettingsScreen'),
                    },
                ],
            },
            {
                key: 'sync',
                title: t('sync_section_title'),
                items: [
                    {
                        key: 'sync-status',
                        title: t('sync_menu_title'),
                        subtitle: t('sync_menu_subtitle', {
                            pending: pendingCount,
                            failed: failedCount,
                            syncing: syncingCount,
                        }),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'sync-circle',
                        onPress: () => navigation.navigate('OfflineSyncStatusScreen'),
                    },
                ],
            },
        ];

        if (isFiscal) {
            result.splice(1, 0, {
                key: 'cash-register',
                title: t('cash_register_functions'),
                items: [
                    {
                        key: 'status-query',
                        title: t('status_query'),
                        subtitle: t('functions_status_query_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'file-chart-outline',
                        disabled: true,
                    },
                    {
                        key: 'daily-query',
                        title: t('daily_query'),
                        subtitle: t('functions_daily_query_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'chart-bar',
                        disabled: true,
                    },
                    {
                        key: 'periodic-query',
                        title: t('periodic_query'),
                        subtitle: t('functions_periodic_query_subtitle'),
                        iconFamily: 'MaterialIcons',
                        icon: 'table-chart',
                        disabled: true,
                    },
                    {
                        key: 'money-in',
                        title: t('money_in'),
                        subtitle: t('functions_money_in_subtitle'),
                        iconFamily: 'MaterialIcons',
                        icon: 'attach-money',
                        disabled: true,
                    },
                    {
                        key: 'money-out',
                        title: t('money_out'),
                        subtitle: t('functions_money_out_subtitle'),
                        iconFamily: 'MaterialIcons',
                        icon: 'money-off',
                        disabled: true,
                    },
                    {
                        key: 'deposit',
                        title: t('deposit'),
                        subtitle: t('functions_deposit_subtitle'),
                        iconFamily: 'MaterialCommunityIcons',
                        icon: 'bottle-soda-classic-outline',
                        disabled: true,
                    },
                ],
            });
        }

        result.push({
            key: 'app-info',
            title: t('app_section_title', 'Alkalmazás'),
            items: [
                {
                    key: 'whats-new',
                    title: t('whats_new_menu_title', "Újdonságok (What's New)"),
                    subtitle: t('whats_new_menu_subtitle', 'Legutóbbi verziófrissítések és új funkciók részletei'),
                    iconFamily: 'MaterialCommunityIcons',
                    icon: 'gift-outline',
                    onPress: () => Observables.call('openWhatsNew'),
                },
            ],
        });

        return result;
    }, [aiAssistantEnabled, failedCount, isFiscal, navigation, openAssistantScreen, openConsumableStock, openDialer, openMailScreen, openTeamChat, pendingCount, procurementEnabled, setShowStand, syncingCount, t, teamChatUnread, teamChatVisible, toggleInventoryModal, toggleTodoModal, unreadCount]);

    return (
        <LinearGradient colors={isDark ? [theme.colors.background, theme.colors.card, theme.colors.background] : ['#EAE5EE', '#FEE6DE', '#EBE5F2']} style={styles.screen}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={[
                    styles.scrollContent,
                    {
                        paddingHorizontal: metrics.screenPadding,
                        paddingTop: width >= 768 ? 26 : 18,
                        paddingBottom: 120,
                    },
                ]}
                showsVerticalScrollIndicator={false}
            >
                <View style={[styles.contentWrap, {maxWidth: metrics.contentMaxWidth}]}> 
                    <View style={styles.heroCard}>
                        <View style={styles.heroTextWrap}>
                            <Text style={styles.heroEyebrow}>{t('functions')}</Text>
                            <Text style={styles.heroTitle}>{t('functions_hero_title')}</Text>
                            <Text style={styles.heroDescription}>{t('functions_hero_description')}</Text>
                            <View style={styles.heroStatusRow}>
                                <TransportStatusBadge transport={activeTransport} />
                                {statusChips.map((chip) => (
                                    <View
                                        key={chip.key}
                                        style={[
                                            styles.heroStatusChip,
                                            chip.active && styles.heroStatusChipActive,
                                        ]}
                                    >
                                        <View style={[
                                            styles.heroStatusDot,
                                            chip.active && styles.heroStatusDotActive,
                                        ]} />
                                        <Text style={[
                                            styles.heroStatusText,
                                            chip.active && styles.heroStatusTextActive,
                                        ]}>
                                            {chip.label}
                                        </Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                        <TouchableOpacity
                            activeOpacity={0.85}
                            onPress={() => navigation.navigate('SettingsScreen')}
                            style={styles.settingsButton}
                        >
                            <Ionicons name="settings-sharp" color={theme.colors.text} size={24} />
                        </TouchableOpacity>
                    </View>

                    <View style={styles.sectionBlock}>
                        <Text style={styles.sectionTitle}>{t('functions_featured_section_title')}</Text>
                        <View style={styles.sectionCard}>
                            {featuredActions.map((item, index) => (
                                <SettingsActionRow
                                    key={item.key}
                                    item={item}
                                    isLast={index === featuredActions.length - 1}
                                    styles={styles}
                                    theme={theme}
                                />
                            ))}
                        </View>
                    </View>

                    {sections.map((section) => (
                        <View key={section.key} style={styles.sectionBlock}>
                            <Text style={styles.sectionTitle}>{section.title}</Text>
                            <View style={styles.sectionCard}>
                                {section.items.map((item, index) => (
                                    <SettingsActionRow
                                        key={item.key}
                                        item={item}
                                        isLast={index === section.items.length - 1}
                                        styles={styles}
                                        theme={theme}
                                    />
                                ))}
                            </View>
                        </View>
                    ))}
                </View>
            </ScrollView>

            {inventoryModalVisible && (
                <Modal
                    animationType="slide"
                    transparent
                    visible={inventoryModalVisible}
                    onRequestClose={toggleInventoryModal}
                >
                    <View style={{flex: 1}}>
                        <View style={styles.modalContent}>
                            <InventoryForm onClose={() => setInventoryModalVisible(false)} />
                        </View>
                    </View>
                </Modal>
            )}
            {showStand && (
                <StandModal
                    isVisible={showStand}
                    saveInventory={console.log}
                    onClose={() => setShowStand(false)}
                />
            )}
            {todoModalVisible && <TodoList visible={todoModalVisible} onClose={toggleTodoModal} />}
            {showdatemodal && (
                <DateTimeModal
                    isVisible={showdatemodal}
                    setDate={console.log}
                    setTime={console.log}
                    isTime
                    onClose={() => setShowDateModal(false)}
                />
            )}
        </LinearGradient>
    );
};

const buildStyles = (theme, isDark) => ({
    screen: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    // Outer ScrollView must own a definite, flexible height so the list scrolls
    // reliably on web/desktop instead of collapsing into a short region. (SVC-008)
    scroll: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    scrollContent: {
        flexGrow: 1,
        alignItems: 'center',
    },
    contentWrap: {
        width: '100%',
    },
    heroCard: {
        backgroundColor: theme.colors.card,
        borderRadius: 24,
        padding: 18,
        marginBottom: 18,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        shadowColor: '#0f172a',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.06,
        shadowRadius: 18,
        elevation: 3,
        borderWidth: 1,
        borderColor: isDark ? theme.colors.border : 'rgba(255,255,255,0.7)',
    },
    heroTextWrap: {
        flex: 1,
        paddingRight: 12,
    },
    heroEyebrow: {
        fontSize: 11,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        fontWeight: '800',
        color: '#c2410c',
    },
    heroTitle: {
        marginTop: 6,
        fontSize: 22,
        lineHeight: 28,
        fontWeight: '800',
        color: theme.colors.text,
    },
    heroDescription: {
        marginTop: 8,
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.subText,
    },
    heroStatusRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 12,
    },
    heroStatusChip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 7,
        borderRadius: 999,
        backgroundColor: theme.colors.inputBackground,
    },
    heroStatusChipActive: {
        backgroundColor: '#ecfdf5',
    },
    heroStatusDot: {
        width: 8,
        height: 8,
        borderRadius: 999,
        backgroundColor: theme.colors.placeholder,
        marginRight: 6,
    },
    heroStatusDotActive: {
        backgroundColor: '#16a34a',
    },
    heroStatusText: {
        fontSize: 12,
        fontWeight: '700',
        color: theme.colors.subText,
    },
    heroStatusTextActive: {
        color: '#166534',
    },
    settingsButton: {
        width: 46,
        height: 46,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(17,24,39,0.05)',
    },
    sectionBlock: {
        marginBottom: 18,
    },
    sectionTitle: {
        marginBottom: 10,
        marginLeft: 2,
        fontSize: 12,
        fontWeight: '800',
        color: theme.colors.subText,
        letterSpacing: 0.7,
        textTransform: 'uppercase',
    },
    sectionCard: {
        overflow: 'hidden',
        backgroundColor: theme.colors.card,
        borderRadius: 20,
        shadowColor: '#0f172a',
        shadowOffset: {width: 0, height: 6},
        shadowOpacity: 0.05,
        shadowRadius: 14,
        elevation: 2,
        borderWidth: 1,
        borderColor: isDark ? theme.colors.border : 'rgba(255,255,255,0.72)',
    },
    settingsRow: {
        minHeight: 74,
        paddingHorizontal: 16,
        paddingVertical: 14,
        flexDirection: 'row',
        alignItems: 'center',
    },
    settingsRowBorder: {
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: theme.colors.border,
    },
    settingsRowHighlight: {
        backgroundColor: isDark ? 'rgba(249,115,22,0.12)' : '#fff7ed',
    },
    settingsRowDisabled: {
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(249,250,251,0.88)',
    },
    rowIconBadge: {
        width: 42,
        height: 42,
        borderRadius: 13,
        backgroundColor: theme.colors.inputBackground,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    rowIconBadgeHighlight: {
        backgroundColor: isDark ? 'rgba(249,115,22,0.18)' : '#ffedd5',
    },
    rowIconBadgeDisabled: {
        backgroundColor: theme.colors.inputBackground,
    },
    rowContent: {
        flex: 1,
        minWidth: 0,
    },
    rowTitle: {
        fontSize: 15,
        lineHeight: 19,
        fontWeight: '800',
        color: theme.colors.text,
    },
    rowTitleHighlight: {
        color: theme.colors.text,
    },
    rowTitleDisabled: {
        color: theme.colors.subText,
    },
    rowSubtitle: {
        marginTop: 3,
        fontSize: 12,
        lineHeight: 17,
        color: theme.colors.subText,
    },
    rowSubtitleHighlight: {
        color: '#9a3412',
    },
    disabledPill: {
        width: 30,
        height: 30,
        borderRadius: 15,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.inputBackground,
        marginLeft: 12,
    },
    modalContent: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.2)',
        justifyContent: 'center',
    },
});

export default FunctionsScreen;
